import { Group, Select, Stack, TextInput } from '@mantine/core';
import { DatePickerInput, type DatesRangeValue } from '@mantine/dates';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { inkLight } from '../../../../styles/design-tokens';
import { Divider } from '../../../../ui-library/divider/divider';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';
import { toDateOrNull, toIsoDateOrNull } from './session-filters.helpers';
import { DATE_OPTIONS, type DateRangePreset, type SessionsFiltersValue } from './session-filters.type';

type FiltersHeaderSectionProps = {
  value: SessionsFiltersValue;
  setQ: (value: string) => void;
  setDateRange: (value: DateRangePreset) => void;
  setCustomRange: (start: string | null, end: string | null) => void;
};

export function FiltersHeaderSection({ value, setQ, setDateRange, setCustomRange }: FiltersHeaderSectionProps) {
  const { t } = useTranslation();

  return (
    <Stack gap={16}>
      <TextInput
        placeholder={`Search name, directory, or ${t('common.work_unit')}…`}
        value={value.q}
        onChange={(event) => setQ(event.currentTarget.value)}
        size="sm"
        radius="xl"
      />
      <Divider />
      <Stack gap={16}>
        <Group gap="xs">
          <Icon name="schedule" size={16} color={inkLight} />
          <Text weight="medium" size="small">
            Started
          </Text>
        </Group>
        <Select
          data={DATE_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
          value={value.dateRange}
          onChange={(next) => setDateRange((next ?? '30d') as DateRangePreset)}
          size="sm"
          radius="xl"
          allowDeselect={false}
        />
        {value.dateRange === 'custom' && (
          <DatePickerInput
            type="range"
            placeholder="Pick dates"
            value={[toDateOrNull(value.customStart), toDateOrNull(value.customEnd)]}
            onChange={(range) => {
              const [start, end] = range as DatesRangeValue;
              setCustomRange(toIsoDateOrNull(start), toIsoDateOrNull(end));
            }}
            valueFormatter={({ date }) => {
              if (!Array.isArray(date)) return '';
              const [start, end] = date;
              if (!start || !end) return '';
              return `${dayjs(start).format('MM/DD/YYYY')} – ${dayjs(end).format('MM/DD/YYYY')}`;
            }}
            maxDate={new Date()}
            size="sm"
            radius="xl"
            clearable
          />
        )}
      </Stack>
    </Stack>
  );
}
